/* eslint-disable no-console */
import Cookies from "js-cookie";
import {useState, useRef} from "react";

export interface UploadedFile {
    id: string;
    filename: string;
    contentType: string;
    size: number;
    isImage: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function useFileUpload() {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const uploadFile = async (file: File): Promise<UploadedFile | null> => {
        if (file.size > MAX_FILE_SIZE) {
            alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
            return null;
        }
        const csrfToken = Cookies.get("csrftoken") || "";
        const formData = new FormData();
        formData.append("file", file);

        setIsUploading(true);
        try {
            const response = await fetch("/api/files/upload/", {
                method: "POST",
                headers: {"X-CSRFToken": csrfToken},
                credentials: "include",
                body: formData,
            });

            if (!response.ok) {
                const err = await response.json().catch(() => null);
                const detail = err?.error || err?.detail || `Upload failed (HTTP ${response.status})`;
                console.error("File upload error:", response.status, err);
                alert(detail);
                return null;
            }

            const data = await response.json();
            return {
                id: data.id,
                filename: data.filename,
                contentType: data.contentType,
                size: data.size,
                isImage: data.isImage,
            };
        } catch (e) {
            console.error("File upload network error:", e);
            alert(`Failed to upload file: ${e instanceof Error ? e.message : "network error"}`);
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles) return;

        for (const file of Array.from(selectedFiles)) {
            const uploaded = await uploadFile(file);
            if (uploaded) {
                setFiles((prev) => [...prev, uploaded]);
            }
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeFile = (fileId: string) => {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
    };

    const clearFiles = () => setFiles([]);

    const openFilePicker = () => fileInputRef.current?.click();

    return {
        files,
        isUploading,
        fileInputRef,
        handleFileSelect,
        removeFile,
        clearFiles,
        openFilePicker,
    };
}
